import { useEffect, useRef, useState } from 'react'

import { submitAIConversationMessageFeedback } from '../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'

import {
  getGretaAIChatFeedbackPayload,
  getGretaAIChatFeedbackWithoutMessage,
  getGretaAIChatRefreshedFeedback,
} from './useGretaAIChatFeedback.utils'

export function useGretaAIChatFeedback({
  activeChatId,
  messages = EMPTY_LIST,
}) {
  const [serverFeedbackByMessageId, setServerFeedbackByMessageId] =
    useState(EMPTY_OBJECT)
  const [localFeedbackByMessageId, setLocalFeedbackByMessageId] =
    useState(EMPTY_OBJECT)
  const [ratedChatId, setRatedChatId] = useState(activeChatId)
  const [loadedMessages, setLoadedMessages] = useState(null)

  const activeChatIdRef = useRef(activeChatId)
  const submissionTokenByMessageIdRef = useRef(new Map())

  useEffect(() => {
    activeChatIdRef.current = activeChatId
  }, [activeChatId])

  if (ratedChatId !== activeChatId) {
    setRatedChatId(activeChatId)
    setServerFeedbackByMessageId(EMPTY_OBJECT)
    setLocalFeedbackByMessageId(EMPTY_OBJECT)
    setLoadedMessages(null)
  }

  if (messages !== loadedMessages) {
    const previousMessages = loadedMessages ?? EMPTY_LIST

    setLoadedMessages(messages)
    setServerFeedbackByMessageId(current =>
      getGretaAIChatRefreshedFeedback({ current, messages, previousMessages })
    )
  }

  async function submitFeedback(messageId, { comment, reason, value }) {
    if (!value || localFeedbackByMessageId[messageId]?.isSubmitting) {
      return
    }

    const conversationId = activeChatId
    const token = {}

    submissionTokenByMessageIdRef.current.set(messageId, token)

    function isOwnOutcome() {
      return (
        submissionTokenByMessageIdRef.current.get(messageId) === token &&
        activeChatIdRef.current === conversationId
      )
    }

    setLocalFeedbackByMessageId(current => ({
      ...current,
      [messageId]: { comment, isSubmitting: true, reason, value },
    }))

    try {
      await submitAIConversationMessageFeedback({
        conversationId,
        data: getGretaAIChatFeedbackPayload({ comment, reason, value }),
        messageId,
      })

      if (isOwnOutcome()) {
        submissionTokenByMessageIdRef.current.delete(messageId)
        setServerFeedbackByMessageId(current => ({
          ...current,
          [messageId]: { comment, reason, value },
        }))
        setLocalFeedbackByMessageId(current =>
          getGretaAIChatFeedbackWithoutMessage(current, messageId)
        )
      }
    } catch {
      if (isOwnOutcome()) {
        submissionTokenByMessageIdRef.current.delete(messageId)
        setLocalFeedbackByMessageId(current =>
          getGretaAIChatFeedbackWithoutMessage(current, messageId)
        )
      }
    }
  }

  return {
    feedbackByMessageId: {
      ...serverFeedbackByMessageId,
      ...localFeedbackByMessageId,
    },
    submitFeedback,
  }
}
